/*
 **	OMNI_IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 **
 **	Hierarchical Location Tree Picker Component
 **
 **	Author(s): Claude Code Assistant
 */

import React from 'react'
import { MaterialIcon } from './ui/MaterialIcon'
import { OCAColors, Typography, Spacing } from '../theme/colors'
import type { Customer, Site, Project, Area } from '../types/locations'

interface HierarchicalLocationPickerProps {
  customers: Customer[]
  expandedLocations: Set<string>
  selectedLocationId?: string
  onToggleExpansion: (locationId: string) => void
  onSelectLocation: (locationId: string, locationName: string, path: string) => void
}

export const HierarchicalLocationPicker: React.FC<HierarchicalLocationPickerProps> = ({
  customers,
  expandedLocations,
  selectedLocationId,
  onToggleExpansion,
  onSelectLocation
}) => {
  const renderArea = (area: Area, parentPath: string, level: number) => {
    const path = `${parentPath} > ${area.name || area.locationId}`
    const isSelected = selectedLocationId === area.locationId

    return (
      <div
        key={area.locationId}
        onClick={() => onSelectLocation(area.locationId, area.name || area.locationId, path)}
        style={{
          padding: Spacing.md,
          paddingLeft: `${16 + level * 20}px`,
          borderBottom: `1px solid ${OCAColors.separator}`,
          cursor: 'pointer',
          backgroundColor: isSelected ? OCAColors.backgroundSecondary : OCAColors.background
        }}
      >
        <div style={{
          fontSize: Typography.fontSize.base,
          color: OCAColors.label,
          fontWeight: isSelected ? Typography.fontWeight.semibold : Typography.fontWeight.normal
        }}>
          {area.name || area.locationId}
        </div>
        <div style={{
          fontSize: Typography.fontSize.sm,
          color: OCAColors.secondaryLabel
        }}>
          Area
        </div>
      </div>
    )
  }

  const renderProject = (project: Project, customerName: string, siteName?: string, level: number = 2) => {
    const path = siteName
      ? `${customerName} > ${siteName} > ${project.name || project.locationId}`
      : `${customerName} > ${project.name || project.locationId}`
    const isExpanded = expandedLocations.has(project.locationId)
    const hasAreas = project.areas && project.areas.length > 0
    const isSelected = selectedLocationId === project.locationId

    return (
      <div key={project.locationId}>
        <div
          onClick={() => {
            if (hasAreas) {
              onToggleExpansion(project.locationId)
            } else {
              onSelectLocation(project.locationId, project.name || project.locationId, path)
            }
          }}
          style={{
            padding: Spacing.md,
            paddingLeft: `${16 + level * 20}px`,
            borderBottom: `1px solid ${OCAColors.separator}`,
            cursor: 'pointer',
            backgroundColor: isSelected ? OCAColors.backgroundSecondary : OCAColors.background,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div>
            <div style={{
              fontSize: Typography.fontSize.base,
              color: OCAColors.label,
              fontWeight: isSelected ? Typography.fontWeight.semibold : Typography.fontWeight.normal
            }}>
              {project.name || project.locationId}
            </div>
            <div style={{
              fontSize: Typography.fontSize.sm,
              color: OCAColors.secondaryLabel
            }}>
              Project
            </div>
          </div>
          {hasAreas && (
            <MaterialIcon
              name={isExpanded ? 'expand_less' : 'expand_more'}
              size={20}
              color={OCAColors.secondaryLabel}
            />
          )}
        </div>

        {isExpanded && hasAreas && project.areas?.map(area => renderArea(area, path, level + 1))}
      </div>
    )
  }

  const renderSite = (site: Site, customerName: string, level: number = 1) => {
    const path = `${customerName} > ${site.name || site.locationId}`
    const isExpanded = expandedLocations.has(site.locationId)
    const hasChildren = (site.areas && site.areas.length > 0) || (site.projects && site.projects.length > 0)
    const isSelected = selectedLocationId === site.locationId

    return (
      <div key={site.locationId}>
        <div
          onClick={() => {
            if (hasChildren) {
              onToggleExpansion(site.locationId)
            } else {
              onSelectLocation(site.locationId, site.name || site.locationId, path)
            }
          }}
          style={{
            padding: Spacing.md,
            paddingLeft: `${16 + level * 20}px`,
            borderBottom: `1px solid ${OCAColors.separator}`,
            cursor: 'pointer',
            backgroundColor: isSelected ? OCAColors.backgroundSecondary : OCAColors.background,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div>
            <div style={{
              fontSize: Typography.fontSize.base,
              color: OCAColors.label,
              fontWeight: isSelected ? Typography.fontWeight.semibold : Typography.fontWeight.normal
            }}>
              {site.name || site.locationId}
            </div>
            <div style={{
              fontSize: Typography.fontSize.sm,
              color: OCAColors.secondaryLabel
            }}>
              Site
            </div>
          </div>
          {hasChildren && (
            <MaterialIcon
              name={isExpanded ? 'expand_less' : 'expand_more'}
              size={20}
              color={OCAColors.secondaryLabel}
            />
          )}
        </div>

        {isExpanded && (
          <>
            {site.areas?.map(area => renderArea(area, path, level + 1))}
            {site.projects?.map(project => renderProject(project, customerName, site.name, level + 1))}
          </>
        )}
      </div>
    )
  }

  const renderCustomer = (customer: Customer) => {
    const path = customer.name || customer.locationId
    const isExpanded = expandedLocations.has(customer.locationId)
    const hasChildren = (customer.sites && customer.sites.length > 0) || (customer.projects && customer.projects.length > 0)
    const isSelected = selectedLocationId === customer.locationId

    return (
      <div key={customer.locationId}>
        <div
          onClick={() => {
            if (hasChildren) {
              onToggleExpansion(customer.locationId)
            } else {
              onSelectLocation(customer.locationId, customer.name || customer.locationId, path)
            }
          }}
          style={{
            padding: Spacing.md,
            paddingLeft: '16px',
            borderBottom: `1px solid ${OCAColors.separator}`,
            cursor: 'pointer',
            backgroundColor: isSelected ? OCAColors.backgroundSecondary : OCAColors.background,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div>
            <div style={{
              fontSize: Typography.fontSize.base,
              color: OCAColors.label,
              fontWeight: isSelected ? Typography.fontWeight.semibold : Typography.fontWeight.normal
            }}>
              {customer.name || customer.locationId}
            </div>
            <div style={{
              fontSize: Typography.fontSize.sm,
              color: OCAColors.secondaryLabel
            }}>
              Customer
            </div>
          </div>
          {hasChildren && (
            <MaterialIcon
              name={isExpanded ? 'expand_less' : 'expand_more'}
              size={20}
              color={OCAColors.secondaryLabel}
            />
          )}
        </div>

        {isExpanded && (
          <>
            {customer.sites?.map(site => renderSite(site, customer.name || customer.locationId))}
            {customer.projects?.map(project => renderProject(project, customer.name || customer.locationId))}
          </>
        )}
      </div>
    )
  }

  return (
    <div style={{
      maxHeight: '300px',
      overflowY: 'auto',
      border: `1px solid ${OCAColors.separator}`,
      borderRadius: '8px',
      marginTop: Spacing.xs,
      backgroundColor: OCAColors.background
    }}>
      {customers.map(customer => renderCustomer(customer))}
    </div>
  )
}
